import { useMemo } from 'react';

import { Link, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';

import { MatterAuditTrail } from './audit-trail';
import { Uploads } from './file-history';
import { Issues } from './issues';
import { Metadata } from './metadata';
import { Relativity } from './relativity';
import { Users } from './users';
import { Permission } from '../../../../../../../backend/src/auth/permissions/permission';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useUserContext } from '@/Contexts/User';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { useMatterBreadcrumbHead } from '@/new/components/breadcrumb-dropdown';
import { Icon } from '@/new/components/icon';
import { SettingsNav, SettingsNavGroup, SettingsNavItem, SettingsWrapper } from '@/new/components/settings-nav';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';

enum SettingsTab {
  Metadata = 'metadata',
  Context = 'context',
  Relativity = 'relativity',
  FileHistory = 'file-history',
  Users = 'users',
  MatterAuditTrail = 'audit-trail',
}

interface SettingsTabDef {
  id: SettingsTab;
  title: string;
  icon: React.ReactNode;
  permission?: Permission | Permission[];
  /** If true, non-seat-based matter creators are allowed access regardless of `permission`. */
  allowCreator?: boolean;
  uiMode?: 'hidden' | 'disabled';
  disabledMessage?: string;
}

const tabs: SettingsTabDef[] = [
  {
    id: SettingsTab.Metadata,
    title: 'Metadata',
    icon: <Icon className="lucide--info" />,
    permission: Permission.MatterEdit,
    allowCreator: true,
  },
  {
    id: SettingsTab.Context,
    title: 'Context',
    icon: <Icon className="lucide--scroll-text" />,
    permission: Permission.MatterEdit,
    allowCreator: true,
  },
  {
    id: SettingsTab.Relativity,
    title: 'Relativity',
    icon: <Icon className="lucide--squares-unite" />,
    permission: Permission.RelativityImport,
  },
  {
    id: SettingsTab.Users,
    title: 'Users',
    icon: <Icon className="lucide--users-round" />,
    permission: Permission.MatterViewUsers,
  },
  {
    id: SettingsTab.FileHistory,
    title: 'File History',
    icon: <Icon className="lucide--file-clock" />,
    permission: Permission.MatterCreate,
  },
  {
    id: SettingsTab.MatterAuditTrail,
    title: 'Audit Trail',
    icon: <Icon className="lucide--clock" />,
    permission: Permission.AuditView,
  },
];

const nestedSegmentLabels: Record<string, string> = {
  'load-file-import': 'Load File Import',
  sync: 'Sync',
};

function hasAnyPermission(checkPermission: (p: Permission) => boolean, permission: Permission | Permission[]) {
  const permissions = Array.isArray(permission) ? permission : [permission];
  return permissions.some(checkPermission);
}

function PermissionRoute({
  permission,
  allowCreator = false,
  children,
}: {
  permission: Permission | Permission[];
  allowCreator?: boolean;
  children: React.ReactNode;
}) {
  const { matterId } = useParams();
  const { user } = useUserContext();
  const { hasPermission: checkPermission } = useHasPermission();
  const { data: matter } = useGetMatterV2(matterId);

  // Wait for user data before making a permission decision
  if (!user) return null;

  // Wait for matter data before decision is made
  if (allowCreator && !matter) return null;

  const isCreator = allowCreator && matter?.createdById === user.id;
  const isSeatBasedCreator = isCreator && matter?.isSeatBased;
  if (!hasAnyPermission(checkPermission, permission) && !isSeatBasedCreator) {
    return <Navigate to={`/matters/${matterId}`} replace />;
  }

  return <>{children}</>;
}

function SettingsRedirect({ isSeatBasedCreator }: { isSeatBasedCreator: boolean }) {
  const { hasPermission: checkPermission } = useHasPermission();

  const firstAllowedTab = tabs.find(
    (tab) =>
      !tab.permission || hasAnyPermission(checkPermission, tab.permission) || (tab.allowCreator && isSeatBasedCreator),
  );

  if (firstAllowedTab) {
    return <Navigate to={firstAllowedTab.id} replace />;
  }

  return <Navigate to="/matters" replace />;
}

function SettingsTabItem({
  tab,
  isSeatBasedCreator,
  isActive,
}: {
  tab: SettingsTabDef;
  isSeatBasedCreator: boolean;
  isActive: boolean;
}) {
  const { hasPermission: checkPermission } = useHasPermission();
  const allowed =
    !tab.permission || hasAnyPermission(checkPermission, tab.permission) || (tab.allowCreator && isSeatBasedCreator);
  const uiMode = tab.uiMode ?? 'hidden';
  const isHidden = !allowed && uiMode === 'hidden';
  const isDisabled = !allowed && uiMode === 'disabled';

  if (isHidden) return null;

  if (isDisabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div>
            <SettingsNavItem disabled>
              {tab.icon}
              <span>{tab.title}</span>
            </SettingsNavItem>
          </div>
        </TooltipTrigger>
        <TooltipContent>{tab.disabledMessage ?? 'You do not have permission to access this'}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <SettingsNavItem asChild active={isActive}>
      <Link to={tab.id}>
        {tab.icon}
        <span>{tab.title}</span>
      </Link>
    </SettingsNavItem>
  );
}

export function MatterSettings() {
  const { matterId } = useParams();
  if (!matterId) return null;

  const { data: matter } = useGetMatterV2(matterId);
  const { user } = useUserContext();
  const matterBreadcrumbHead = useMatterBreadcrumbHead(matterId, matter?.name, false);
  const isCreator = !!matter && !!user && matter.createdById === user.id;
  const isSeatBasedCreator = isCreator && matter?.isSeatBased;

  const location = useLocation();

  const activeTab = useMemo(() => {
    const pathSegments = location.pathname.split('/');

    for (const tab of tabs) {
      if (pathSegments.includes(tab.id)) {
        return tab.id;
      }
    }
  }, [location.pathname]);

  // Build the breadcrumb trail from the URL segments below /settings, e.g.
  // Settings / Relativity / Load File Import. Dynamic segments (ids) are skipped.
  const settingsBreadcrumbs = useMemo(() => {
    const settingsHref = `/matters/${matterId}/settings`;
    const segments = location.pathname.split('/').filter(Boolean);
    const rest = segments.slice(segments.indexOf('settings') + 1);

    const items = [{ href: settingsHref, label: 'Settings', active: false }];
    let href = settingsHref;
    for (const segment of rest) {
      href += `/${segment}`;
      const label = tabs.find((tab) => tab.id === segment)?.title ?? nestedSegmentLabels[segment];
      if (label) items.push({ href, label, active: false });
    }
    items[items.length - 1].active = true;
    return items;
  }, [location.pathname, matterId]);

  return (
    <div className="flex h-full flex-col">
      <Breadcrumb items={[...matterBreadcrumbHead, ...settingsBreadcrumbs]} />

      <SettingsWrapper>
        <SettingsNav className="w-56">
          <SettingsNavGroup>
            {tabs.map((tab) => (
              <SettingsTabItem
                key={tab.id}
                tab={tab}
                isSeatBasedCreator={isSeatBasedCreator}
                isActive={activeTab === tab.id}
              />
            ))}
          </SettingsNavGroup>
        </SettingsNav>

        <div className="flex min-h-0 flex-1 flex-col overflow-auto p-7 px-8">
          <Routes>
            <Route index element={<SettingsRedirect isSeatBasedCreator={isSeatBasedCreator} />} />
            <Route
              path="metadata"
              element={
                <PermissionRoute permission={Permission.MatterEdit} allowCreator>
                  <Metadata />
                </PermissionRoute>
              }
            />
            <Route
              path="context"
              element={
                <PermissionRoute permission={Permission.MatterEdit} allowCreator>
                  <Issues />
                </PermissionRoute>
              }
            />
            <Route
              path="relativity/*"
              element={
                <PermissionRoute permission={Permission.RelativityImport}>
                  <Relativity />
                </PermissionRoute>
              }
            />
            <Route
              path="file-history/*"
              element={
                <PermissionRoute permission={Permission.MatterCreate}>
                  <Uploads />
                </PermissionRoute>
              }
            />
            <Route
              path="users"
              element={
                <PermissionRoute permission={Permission.MatterViewUsers}>
                  <Users />
                </PermissionRoute>
              }
            />
            <Route
              path="audit-trail"
              element={
                <PermissionRoute permission={Permission.AuditView}>
                  <MatterAuditTrail />
                </PermissionRoute>
              }
            />
          </Routes>
        </div>
      </SettingsWrapper>
    </div>
  );
}
